/**
 * Sistema de permisos por rol
 * Define qué acciones puede realizar cada rol en el sistema
 */

// IDs de roles (deben coincidir con los de la base de datos)
export const ROLES = {
  ADMINISTRADOR: 1,
  DONANTE: 2,
  TESORERO: 3
};

// Nombres de roles para display
export const ROLE_NAMES = {
  [ROLES.ADMINISTRADOR]: 'Administrador',
  [ROLES.DONANTE]: 'Donante',
  [ROLES.TESORERO]: 'Tesorero'
};

// Definición de permisos por módulo
export const PERMISSIONS = {
  DONANTES: {
    VIEW: 'donantes.view',
    CREATE: 'donantes.create',
    EDIT: 'donantes.edit',
    DELETE: 'donantes.delete'
  },
  INGRESOS: {
    VIEW: 'ingresos.view',
    CREATE: 'ingresos.create',
    EDIT: 'ingresos.edit',
    DELETE: 'ingresos.delete'
  },
  EGRESOS: {
    VIEW: 'egresos.view',
    CREATE: 'egresos.create',
    EDIT: 'egresos.edit',
    DELETE: 'egresos.delete'
  },
  REPORTES: {
    VIEW_INGRESOS: 'reportes.ingresos',
    VIEW_EGRESOS: 'reportes.egresos',
    VIEW_CONSOLIDADO: 'reportes.consolidado'
  }
};

// Mapa de permisos por rol
const rolePermissions = {
  [ROLES.ADMINISTRADOR]: [
    // Administrador tiene todos los permisos
    PERMISSIONS.DONANTES.VIEW,
    PERMISSIONS.DONANTES.CREATE,
    PERMISSIONS.DONANTES.EDIT,
    PERMISSIONS.DONANTES.DELETE,
    PERMISSIONS.INGRESOS.VIEW,
    PERMISSIONS.INGRESOS.CREATE,
    PERMISSIONS.INGRESOS.EDIT,
    PERMISSIONS.INGRESOS.DELETE,
    PERMISSIONS.EGRESOS.VIEW,
    PERMISSIONS.EGRESOS.CREATE,
    PERMISSIONS.EGRESOS.EDIT,
    PERMISSIONS.EGRESOS.DELETE,
    PERMISSIONS.REPORTES.VIEW_INGRESOS,
    PERMISSIONS.REPORTES.VIEW_EGRESOS,
    PERMISSIONS.REPORTES.VIEW_CONSOLIDADO
  ],
  [ROLES.TESORERO]: [
    // Tesorero tiene todos los permisos EXCEPTO eliminar usuarios
    PERMISSIONS.DONANTES.VIEW,
    PERMISSIONS.DONANTES.CREATE,
    PERMISSIONS.DONANTES.EDIT,
    // NO puede eliminar donantes
    PERMISSIONS.INGRESOS.VIEW,
    PERMISSIONS.INGRESOS.CREATE,
    PERMISSIONS.INGRESOS.EDIT,
    PERMISSIONS.INGRESOS.DELETE,
    PERMISSIONS.EGRESOS.VIEW,
    PERMISSIONS.EGRESOS.CREATE,
    PERMISSIONS.EGRESOS.EDIT,
    PERMISSIONS.EGRESOS.DELETE,
    PERMISSIONS.REPORTES.VIEW_INGRESOS,
    PERMISSIONS.REPORTES.VIEW_EGRESOS,
    PERMISSIONS.REPORTES.VIEW_CONSOLIDADO
  ],
  [ROLES.DONANTE]: [
    // Donante solo puede ver sus propios datos
    PERMISSIONS.INGRESOS.VIEW,
    PERMISSIONS.REPORTES.VIEW_INGRESOS
  ]
};

/**
 * Verifica si un rol tiene un permiso específico
 * @param {number} roleId - ID del rol a verificar
 * @param {string} permission - Permiso a verificar (ej: PERMISSIONS.DONANTES.CREATE)
 * @returns {boolean} - true si tiene el permiso, false si no
 */
export const hasPermission = (roleId, permission) => {
  const permissions = rolePermissions[roleId] || [];
  return permissions.includes(permission);
};

/**
 * Obtiene todos los permisos de un rol
 * @param {number} roleId - ID del rol
 * @returns {Array<string>} - Array de permisos
 */
export const getRolePermissions = (roleId) => {
  return rolePermissions[roleId] || [];
};

/**
 * Verifica si un rol puede gestionar usuarios (crear/editar/eliminar donantes)
 * @param {number} roleId - ID del rol
 * @returns {boolean}
 */
export const canManageUsers = (roleId) => {
  return hasPermission(roleId, PERMISSIONS.DONANTES.CREATE) &&
         hasPermission(roleId, PERMISSIONS.DONANTES.EDIT) &&
         hasPermission(roleId, PERMISSIONS.DONANTES.DELETE);
};

/**
 * Verifica si un rol puede ver la sección de donantes
 * @param {number} roleId - ID del rol
 * @returns {boolean}
 */
export const canViewDonantes = (roleId) => {
  return hasPermission(roleId, PERMISSIONS.DONANTES.VIEW);
};

/**
 * Verifica si un rol puede gestionar ingresos
 * @param {number} roleId - ID del rol
 * @returns {boolean}
 */
export const canManageIngresos = (roleId) => {
  return hasPermission(roleId, PERMISSIONS.INGRESOS.CREATE) ||
         hasPermission(roleId, PERMISSIONS.INGRESOS.EDIT) ||
         hasPermission(roleId, PERMISSIONS.INGRESOS.DELETE);
};

/**
 * Verifica si un rol puede gestionar egresos
 * @param {number} roleId - ID del rol
 * @returns {boolean}
 */
export const canManageEgresos = (roleId) => {
  return hasPermission(roleId, PERMISSIONS.EGRESOS.CREATE) ||
         hasPermission(roleId, PERMISSIONS.EGRESOS.EDIT) ||
         hasPermission(roleId, PERMISSIONS.EGRESOS.DELETE);
};

/**
 * Verifica si un rol puede ver reportes
 * @param {number} roleId - ID del rol
 * @returns {boolean}
 */
export const canViewReportes = (roleId) => {
  return hasPermission(roleId, PERMISSIONS.REPORTES.VIEW_INGRESOS) ||
         hasPermission(roleId, PERMISSIONS.REPORTES.VIEW_EGRESOS) ||
         hasPermission(roleId, PERMISSIONS.REPORTES.VIEW_CONSOLIDADO);
};

// Crear objeto para export default
const permissionsModule = {
  ROLES,
  ROLE_NAMES,
  PERMISSIONS,
  hasPermission,
  getRolePermissions,
  canManageUsers,
  canViewDonantes,
  canManageIngresos,
  canManageEgresos,
  canViewReportes
};

export default permissionsModule;
